import * as React from "react";
import { Redirect, Route, useLocation } from "wouter";
import { useAuth } from "@/contexts/AuthContext";
import {
  getNumericUserLevel,
  getNumericUserStage,
  getUserPrivileges,
  hasAnyPrivilege,
  hasSuperAdminUserLevel,
  type UserStage,
} from "@/utils/privileges";
import { AccessDeniedPage } from "./AccessDeniedPage";
import type { AdminPrivilege } from "@/services/adminService";
import type { User } from "@/services/authService";

interface ProtectedRouteProps {
  path: string;
  component: React.ComponentType;
  requiredPrivileges?: AdminPrivilege[];
  /**
   * Must have at least one of these on active admin assignments (no superadmin bypass).
   * Use for routes like Daftar Anggota that require real `manage_users` on an assignment.
   */
  requiredAssignmentPrivileges?: AdminPrivilege[];
  /** Logged-in users with one of these levels are sent to /user */
  blockUserLevels?: number[];
  /** When true, bypass privilege check (still must be authenticated) */
  allowIf?: (user: User) => boolean;
  /**
   * Require this admin scope stage (3 = nasional). Legacy userLevel 3 also satisfies.
   * Checked after `requiredPrivileges` / `requiredAssignmentPrivileges`.
   */
  requiredUserStage?: UserStage;
}

function ProtectedRouteWrapper({ 
  component: Component,
  requiredPrivileges,
  requiredAssignmentPrivileges,
  blockUserLevels,
  allowIf,
  requiredUserStage,
}: { 
  component: React.ComponentType;
  requiredPrivileges?: AdminPrivilege[];
  requiredAssignmentPrivileges?: AdminPrivilege[];
  blockUserLevels?: number[];
  allowIf?: (user: User) => boolean;
  requiredUserStage?: UserStage;
}) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const [, setLocation] = useLocation();

  React.useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      setLocation("/login");
    }
  }, [isLoading, isAuthenticated, setLocation]);

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent"></div>
          <p className="mt-4 text-muted-foreground">Redirecting...</p>
        </div>
      </div>
    );
  }

  if (blockUserLevels && blockUserLevels.length > 0 && user != null) {
    const lv = getNumericUserLevel(user);
    if (blockUserLevels.includes(lv)) {
      return <Redirect href="/user" />;
    }
  }

  if (requiredAssignmentPrivileges && requiredAssignmentPrivileges.length > 0 && user) {
    const privs = getUserPrivileges(user);
    const ok = requiredAssignmentPrivileges.some((p) => privs.includes(p));
    if (!ok) {
      return <AccessDeniedPage requiredPrivileges={requiredAssignmentPrivileges} />;
    }
  }

  if (user && allowIf?.(user)) {
    return <Component />;
  }

  // Check privileges if required
  if (requiredPrivileges && requiredPrivileges.length > 0) {
    const hasAccess = hasAnyPrivilege(user, requiredPrivileges);
    if (!hasAccess) {
      return <AccessDeniedPage requiredPrivileges={requiredPrivileges} />;
    }
  }

  if (requiredUserStage != null && user) {
    const stageOk =
      getNumericUserStage(user) === requiredUserStage || hasSuperAdminUserLevel(user);
    if (!stageOk) {
      return (
        <AccessDeniedPage
          requiredPrivileges={requiredPrivileges}
          message="Halaman ini hanya untuk admin tingkat nasional (userStage 3) dengan hak Silent Key."
        />
      );
    }
  }

  return <Component />;
}

export function ProtectedRoute({ 
  path, 
  component: Component,
  requiredPrivileges,
  requiredAssignmentPrivileges,
  blockUserLevels,
  allowIf,
  requiredUserStage,
}: ProtectedRouteProps) {
  return (
    <Route path={path}>
      {() => (
        <ProtectedRouteWrapper
          component={Component}
          requiredPrivileges={requiredPrivileges}
          requiredAssignmentPrivileges={requiredAssignmentPrivileges}
          blockUserLevels={blockUserLevels}
          allowIf={allowIf}
          requiredUserStage={requiredUserStage}
        />
      )}
    </Route>
  );
}
